import type { RunningHubActiveJobSummary, RunningHubFeatureType } from '../types/runningHubJobs';
import { readRunningHubHistoryCacheFromLocalStorage } from './runningHubHistoryScope';

export type RunningHubAutoAckReason = 'server_history_archived' | 'local_history_match';

export type RunningHubAutoAckDecision = {
  shouldAck: boolean;
  reason?: RunningHubAutoAckReason;
  detail?: string;
};

function isUsableImageUrl(raw: unknown): boolean {
  if (typeof raw !== 'string') return false;
  const u = raw.trim();
  return u.startsWith('http://') || u.startsWith('https://') || u.startsWith('data:image/');
}

function historyItemHasResult(
  featureType: RunningHubFeatureType,
  item: Record<string, unknown>,
): boolean {
  if (featureType === 'tone_mapping') {
    return (
      isUsableImageUrl(item.resultSnapshot) ||
      isUsableImageUrl(item.mainSnapshot) ||
      isUsableImageUrl(item.upscaledUrl)
    );
  }
  if (featureType === 'physical_reconstruct') {
    if (isUsableImageUrl(item.upscaledUrl)) return true;
    if (Array.isArray(item.urls)) {
      return item.urls.some((u) => isUsableImageUrl(u));
    }
    return false;
  }
  return isUsableImageUrl(item.upscaledUrl);
}

function historyItemMatchesClientId(itemId: string, clientId: string): boolean {
  return itemId === clientId || itemId.startsWith(`${clientId}-`);
}

/** 本地历史是否已包含该任务 clientItemId 且带有效结果 URL */
export function findLocalRunningHubHistoryMatch(
  job: RunningHubActiveJobSummary,
  userEmail?: string | null,
): { matchedId: string } | null {
  const clientId = job.history_client_item_id?.trim();
  if (!clientId) return null;

  const items = readRunningHubHistoryCacheFromLocalStorage<{ id: string }>(job.feature_type, userEmail);
  for (const row of items) {
    const item = row as Record<string, unknown>;
    const id = typeof item.id === 'string' ? item.id.trim() : '';
    if (!id || !historyItemMatchesClientId(id, clientId)) continue;
    if (!historyItemHasResult(job.feature_type, item)) continue;
    return { matchedId: id };
  }
  return null;
}

/**
 * 是否可安全自动 ack：服务端已归档 history，或本地历史已写入且含结果 URL。
 * 无法确认时不 ack。
 */
export function evaluateRunningHubJobAutoAck(
  job: RunningHubActiveJobSummary,
  userEmail?: string | null,
): RunningHubAutoAckDecision {
  const clientId = job.history_client_item_id?.trim();
  if (!clientId) {
    return { shouldAck: false, detail: 'missing history_client_item_id' };
  }

  if (job.history_archived_at) {
    return {
      shouldAck: true,
      reason: 'server_history_archived',
      detail: job.history_archived_at,
    };
  }

  const local = findLocalRunningHubHistoryMatch(job, userEmail);
  if (local) {
    return {
      shouldAck: true,
      reason: 'local_history_match',
      detail: local.matchedId,
    };
  }

  return { shouldAck: false, detail: 'no server archive or local history match' };
}
